'use client';
import type { ElementType } from 'react';
import { Link } from '@skin-seoul/design-system';
import {
  useLoyaltyService,
  useUser,
  parseMessageWithBold,
  getAuthenticatedMilestoneBannerMessage,
  getSignInBannerSuffix,
  type Currency,
  type ProgressBarResponse,
} from '@skin-seoul/service';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import { LoyaltyPointsBannerSkeleton } from './LoyaltyPointsBannerSkeleton';

type LoyaltyPointsBannerContainerProps = {
  linkComponent?: ElementType;
  currency: Currency;
  progressBar: ProgressBarResponse | null;
};

export const LoyaltyPointsBannerContainer = ({
  linkComponent,
  currency,
  progressBar,
}: LoyaltyPointsBannerContainerProps) => {
  const LinkComponent = linkComponent || Link;
  const { onCartClose } = useCartDrawer();
  const { user, isAuthenticated, isLoading: isUserLoading } = useUser();
  const { useGetCustomerBalance } = useLoyaltyService();

  const { data: balanceData, isLoading: isBalanceLoading } = useGetCustomerBalance({
    customerId: String(user?.userId ?? ''),
    currency,
    enabled: isAuthenticated && Boolean(user?.userId),
  });

  // Show skeleton while loading user or balance data
  if (isUserLoading || isBalanceLoading) {
    return <LoyaltyPointsBannerSkeleton />;
  }

  // Show sign-in banner if not authenticated or no data
  if (!isAuthenticated || !user?.userId || !balanceData) {
    const suffix = getSignInBannerSuffix(progressBar);
    return (
      <div className="w-full bg-primary-75 p-1 text-center rounded-b-[12px]">
        <span className="text-label-medium font-medium text-primary-500">
          <LinkComponent unstyled href="/login" className="underline font-semibold text-primary-500" onClick={onCartClose}>
            Sign in
          </LinkComponent>
          {suffix}
        </span>
      </div>
    );
  }

  const milestoneMessage = getAuthenticatedMilestoneBannerMessage({ progressBar, currency });
  if (milestoneMessage) {
    return (
      <div className="w-full bg-primary-75 p-1 text-center rounded-b-[12px]">
        <span className="text-label-medium font-medium text-primary-500">
          {parseMessageWithBold(milestoneMessage).map((part, i) => (
            <span key={i} className={part.bold ? 'font-semibold' : 'font-medium'}>
              {part.text}
            </span>
          ))}
        </span>
      </div>
    );
  }

  return null;
};
